import { EstimateLineNoticeType } from "@/js/app/enums";
import { LineErrorItem } from "@/js/interfaces/DTOs/LineEdition";
import config from '@/js/config/app.json';

export interface MessageBag {
    main: Array<string>
    lines_error: Record<number, LineErrorItem>
    lines_warning: Record<number, LineErrorItem>
    lines_notice: Record<number, LineErrorItem>
}

export class MessageBagManager {
    private messageBag: MessageBag;

    constructor(messageBag: MessageBag | null = null) {
        if (null === messageBag) {
            messageBag = this.newBag();
        }
        this.messageBag = messageBag;
    }

    /**
     * Retrieve the entire message bag.
     *
     * @returns The entire message bag
     */
    public getMessageBag(): MessageBag {
        return this.messageBag;
    }

    /**
     * Retrieve the array of main messages from the message bag.
     */
    public get mainMessages(): Array<string> {
        return [... this.messageBag.main];
    }

    /**
     * Retrieve the line-specific error messages from the message bag, organized by line id.
     */
    public get lineErrorMessages(): Record<number, LineErrorItem> {
        return { ... this.messageBag.lines_error };
    }

    /**
     * Retrieve the line-specific warning messages from the message bag, organized by line id.
     */
    public get lineWarningMessages(): Record<number, LineErrorItem> {
        return { ... this.messageBag.lines_warning };
    }

    /**
     * Retrieve the line-specific notice messages from the message bag, organized by line id.
     */
    public get lineNoticeMessages(): Record<number, LineErrorItem> {
        return { ... this.messageBag.lines_notice };
    }

    /**
     * Check if there is an error for a specific line.
     *
     * @param lineId The id of the line to check for an error
     * @returns True if there is an error for the specified line, false otherwise
     */
    public hasErrorForLine(lineId: number): boolean {
        return Object.prototype.hasOwnProperty.call(this.messageBag.lines_error, lineId) && this.messageBag.lines_error[lineId].errors.length > 0;
    }

    /**
     * Check if there is a warning for a specific line.
     *
     * @param lineId The id of the line to check for a warning
     * @returns True if there is a warning for the specified line, false otherwise
     */
    public hasWarningForLine(lineId: number): boolean {
        return Object.prototype.hasOwnProperty.call(this.messageBag.lines_warning, lineId) && this.messageBag.lines_warning[lineId].errors.length > 0;
    }

    /**
     * Check if there is a notice for a specific line.
     *
     * @param lineId The id of the line to check for a notice
     * @returns True if there is a notice for the specified line, false otherwise
     */
    public hasNoticeForLine(lineId: number): boolean {
        return Object.prototype.hasOwnProperty.call(this.messageBag.lines_notice, lineId) && this.messageBag.lines_notice[lineId].errors.length > 0;
    }

    /**
     * Check if there is any error, warning, or notice for a specific line.
     *
     * @param lineId The id of the line to check for any message
     * @returns True if there is any message for the specified line, false otherwise
     */
    public hasAnyMessageForLine(lineId: number): boolean {
        return this.hasErrorForLine(lineId) || this.hasWarningForLine(lineId) || this.hasNoticeForLine(lineId);
    }

    /**
     * Check if there is any error in the entire message bag.
     *
     * @returns True if there is any error in the message bag, false otherwise
     */
    public hasAnyError(): boolean {
        return this.messageBag.main.length > 0
            || Object.keys(this.messageBag.lines_error).length > 0;
    }

    /**
     * Check if there is any warning in the entire message bag.
     *
     * @returns True if there is any warning in the message bag, false otherwise
     */
    public hasAnyWarning(): boolean {
        return Object.keys(this.messageBag.lines_warning).length > 0;
    }

    /**
     * Check if there is any notice in the entire message bag.
     *
     * @returns True if there is any notice in the message bag, false otherwise
     */
    public hasAnyNotice(): boolean {
        return Object.keys(this.messageBag.lines_notice).length > 0;
    }

    /**
     * Check if there is any error, warning, or notice in the entire message bag.
     *
     * @returns True if there is any message in the message bag, false otherwise
     */
    public hasAnyMessage(): boolean {
        return this.messageBag.main.length > 0
            || Object.keys(this.messageBag.lines_error).length > 0
            || Object.keys(this.messageBag.lines_warning).length > 0
            || Object.keys(this.messageBag.lines_notice).length > 0;
    }

    /**
     * Retrieve the error object for a specific line. If no error is found, an error is thrown.
     *
     * @param lineId The id of the line for which to retrieve the error
     * @returns The error object for the specified line
     */
    public getErrorObjectForLine(lineId: number): LineErrorItem {
        if (!this.hasErrorForLine(lineId)) {
            throw new Error(`No error found for line with id ${lineId}`);
        }
        return this.messageBag.lines_error[lineId];
    }

    /**
     * Retrieve the warning object for a specific line. If no warning is found, an error is thrown.
     *
     * @param lineId The id of the line for which to retrieve the warning
     * @returns The warning object for the specified line
     */
    public getWarningObjectForLine(lineId: number): LineErrorItem {
        if (!this.hasWarningForLine(lineId)) {
            throw new Error(`No warning found for line with id ${lineId}`);
        }
        return this.messageBag.lines_warning[lineId];
    }

    /**
     * Retrieve the notice object for a specific line. If no notice is found, an error is thrown.
     *
     * @param lineId The id of the line for which to retrieve the notice
     * @returns The notice object for the specified line
     */
    public getNoticeObjectForLine(lineId: number): LineErrorItem {
        if (!this.hasNoticeForLine(lineId)) {
            throw new Error(`No notice found for line with id ${lineId}`);
        }
        return this.messageBag.lines_notice[lineId];
    }

    /**
     * Retrieve the array of error messages for a specific line. If no error is found, an empty array is returned.
     *
     * @param lineId The id of the line for which to retrieve the error messages
     * @returns The array of error messages for the specified line, or an empty array if no error is found
     */
    public getErrorsForLine(line: number): Array<string> {
        if (this.hasErrorForLine(line)) {
            return this.getErrorObjectForLine(line).errors;
        }
        return [];
    }

    /**
     * Retrieve the array of warning messages for a specific line. If no warning is found, an empty array is returned.
     *
     * @param lineId The id of the line for which to retrieve the warning messages
     * @returns The array of warning messages for the specified line, or an empty array if no warning is found
     */
    public getWarningsForLine(line: number): Array<string> {
        if (this.hasWarningForLine(line)) {
            return this.getWarningObjectForLine(line).errors;
        }
        return [];
    }

    /**
     * Retrieve the array of notice messages for a specific line. If no notice is found, an empty array is returned.
     * @param lineId The id of the line for which to retrieve the notice messages
     * @returns The array of notice messages for the specified line, or an empty array if no notice is found
     */
    public getNoticesForLine(line: number): Array<string> {
        if (this.hasNoticeForLine(line)) {
            return this.getNoticeObjectForLine(line).errors;
        }
        return [];
    }

    /**
     * Retrieve all messages (errors, warnings, and notices) for a specific line.
     *
     * @param lineId The id of the line for which to retrieve all messages
     * @returns An array of all messages for the specified line
     */
    public getMessagesForLine(lineId: number): Array<string> {
        const messages: Array<string> = [];
        if (this.hasErrorForLine(lineId)) {
            messages.push(...this.getErrorObjectForLine(lineId).errors);
        }
        if (this.hasWarningForLine(lineId)) {
            messages.push(...this.getWarningObjectForLine(lineId).errors);
        }
        if (this.hasNoticeForLine(lineId)) {
            messages.push(...this.getNoticeObjectForLine(lineId).errors);
        }
        return messages;
    }

    /**
     * Determine the CSS class to apply to the line logo based on the most severe message type (error, warning, or notice) for a specific line.
     *
     * @param lineId The id of the line for which to determine the CSS class for the logo
     * @returns The CSS class to apply to the line logo based on the most severe message type for the specified line, or an empty string if there are no messages
     */
    public getStyleForLineLogo(lineId: number): string {
        switch (this.getHigherSeverityMessageTypeForLine(lineId)) {
            case EstimateLineNoticeType.error:
                return config.components.EstimateLineReader.class.error;
            case EstimateLineNoticeType.warning:
                return config.components.EstimateLineReader.class.warning;
            case EstimateLineNoticeType.notice:
                return config.components.EstimateLineReader.class.info;
            default:
                return "";
        }
    }

    /**
     * Determine the type of the most severe message (error, warning, or notice) for a specific line.
     *
     * @param lineId The id of the line for which to determine the most severe message type
     * @returns The type of the most severe message for the specified line, or null if there are no messages
     */
    public getHigherSeverityMessageTypeForLine(lineId: number): string | null {
        if (this.hasErrorForLine(lineId)) {
            return EstimateLineNoticeType.error;
        }
        if (this.hasWarningForLine(lineId)) {
            return EstimateLineNoticeType.warning;
        }
        if (this.hasNoticeForLine(lineId)) {
            return EstimateLineNoticeType.notice;
        }
        return null;
    }

    /**
     * Update the entire message bag with new messages.
     *
     * @param newMessages The new message bag to replace the current one
     */
    public updateMessages(newMessages: MessageBag): void {
        this.messageBag.main = [...newMessages.main];
        this.messageBag.lines_error = { ...newMessages.lines_error };
        this.messageBag.lines_warning = { ...newMessages.lines_warning };
        this.messageBag.lines_notice = { ...newMessages.lines_notice };
    }

    /**
     * Update only the main messages and specific line new messages.
     *
     * @param lineId The id of the line for which to update the messages
     * @param newMessages The new message bag containing the messages to update for the specified line
     */
    public updateMainAndLineMessages(lineId: number, newMessages: MessageBag) {
        this.updateMainMessages(newMessages.main);
        this.updateMessagesForLine(lineId, newMessages);
    }

    /**
     * Update only the main messages with new messages.
     *
     * @param newMainMessages The new array of main messages to replace the current main messages
     */
    public updateMainMessages(newMainMessages: Array<string>): void {
        this.messageBag.main = [...newMainMessages];
    }

    /**
     * Update the messages for a specific line with new messages. If there are no existing messages for the line, the new messages will not be added.
     *
     * @param lineId The id of the line for which to update the messages
     * @param newMessages The new message bag containing the messages to update for the specified line
     */
    public updateMessagesForLine(lineId: number, newMessages: MessageBag): void {
        if (Object.prototype.hasOwnProperty.call(newMessages.lines_error, lineId)) {
            this.messageBag.lines_error[lineId] = { ...newMessages.lines_error[lineId] };
        } else {
            delete this.messageBag.lines_error[lineId];
        }
        if (Object.prototype.hasOwnProperty.call(newMessages.lines_warning, lineId)) {
            this.messageBag.lines_warning[lineId] = { ...newMessages.lines_warning[lineId] };
        } else {
            delete this.messageBag.lines_warning[lineId];
        }
        if (Object.prototype.hasOwnProperty.call(newMessages.lines_notice, lineId)) {
            this.messageBag.lines_notice[lineId] = { ...newMessages.lines_notice[lineId] };
        } else {
            delete this.messageBag.lines_notice[lineId];
        }
    }

    /**
     * Remove all messages for a specific line. If there are no messages for the line, no action is taken.
     *
     * @param lineId The id of the line for which to remove all messages
     */
    public removeMessagesForLine(lineId: number): void {
        if (this.hasErrorForLine(lineId)) {
            delete this.messageBag.lines_error[lineId];
        }
        if (this.hasWarningForLine(lineId)) {
            delete this.messageBag.lines_warning[lineId];
        }
        if (this.hasNoticeForLine(lineId)) {
            delete this.messageBag.lines_notice[lineId];
        }
    }

    /**
     * Clear all messages from the message bag, resetting it to an empty state.
     */
    public clean(): void {
        this.messageBag = this.newBag();
    }

    /**
     * Create a new empty message bag.
     *
     * @returns A new empty message bag
     */
    private newBag(): MessageBag {
        return {
            main: [],
            lines_error: {},
            lines_warning: {},
            lines_notice: {},
        };
    }
}
